import React, { ChangeEvent } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Card,
  Checkbox,
  Typography,
  useTheme,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Separator,
} from '@ublend-npm/aulaui-core';

import notificationsTypes from './notificationsTypes';

interface NotificationsTableProps {
  settings: {
    push: { [key: string]: boolean };
    email: { [key: string]: boolean };
  };
  toggleSetting: (type: string, value: string, enabled: boolean) => void;
  isFetchingSettings?: boolean;
  isSavingSettings?: boolean;
}

const NotificationsTable: React.FC<NotificationsTableProps> = ({
  settings,
  toggleSetting,
  isFetchingSettings = false,
  isSavingSettings = false,
}) => {
  const theme = useTheme();
  const isLocked = isSavingSettings || isFetchingSettings;

  const handlePushChange =
    (notificationType: any) =>
    (_e: ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
      if (isLocked) return;
      toggleSetting('push', notificationType.value, isChecked);
    };

  const handleEmailChange =
    (notificationType: any) =>
    (_e: ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
      if (isLocked) return;
      toggleSetting('email', notificationType.value, isChecked);
    };

  const renderRow = (notificationType: any) => (
    <TableRow
      key={notificationType.label}
      sx={{
        '& td': { borderBottom: 'none' },
        height: '42px',
      }}
      aria-label={notificationType.label}
    >
      <TableCell
        padding="none"
        sx={{
          pl: 0,
          pr: '24px',
          pb: 1,
          fontSize: '1rem',
          width: '200px',
        }}
        tabIndex={0}
      >
        {notificationType.label}
      </TableCell>

      <TableCell
        padding="none"
        align="center"
        sx={{
          width: '42px',
          height: '42px',
          p: 0,
          pr: 3,
          pb: 1,
        }}
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <Checkbox
            label={`${notificationType.label} push`}
            labelPosition="hidden"
            inputProps={{ 'aria-label': `${notificationType.label} push` }}
            checked={!!settings.push[notificationType.value]}
            // disabled={isLocked} // Do not visually disable
            // @ts-ignore
            onChange={handlePushChange(notificationType)}
            sx={{
              color: theme.palette.primary.main,
              cursor: isLocked ? 'not-allowed' : 'pointer',
              '& .MuiFormControlLabel-label': {
                display: 'none',
              },
              '&.MuiFormControlLabel-root': {
                margin: 0,
              },
            }}
          />
        </Box>
      </TableCell>

      <TableCell
        padding="none"
        align="center"
        sx={{
          width: '42px',
          height: '42px',
          p: 0,
          pb: 1,
        }}
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <Checkbox
            label={`${notificationType.label} email`}
            labelPosition="hidden"
            inputProps={{ 'aria-label': `${notificationType.label} email` }}
            checked={!!settings.email[notificationType.value]}
            // disabled={isLocked} // Do not visually disable
            // @ts-ignore
            onChange={handleEmailChange(notificationType)}
            sx={{
              color: theme.palette.primary.main,
              cursor: isLocked ? 'not-allowed' : 'pointer',
              // 👇 hide label and its spacing
              '& .MuiFormControlLabel-label': {
                display: 'none',
              },
              '&.MuiFormControlLabel-root': {
                margin: 0,
              },
            }}
          />
        </Box>
      </TableCell>
    </TableRow>
  );

  return (
    <Card
      sx={{
        flex: 1,
        pt: 4,
        px: { xs: 2, md: 3 },
        pb: 3,
        boxShadow: 'none',
        '--Paper-shadow': 'none !important',
        '--Paper-overlay': 'unset !important',
      }}
    >
      <Typography
        variant="h5"
        mb={3}
        sx={{ fontSize: '1.5rem' }}
        aria-label="Notification preferences"
        tabIndex={0}
      >
        Notification preferences
      </Typography>

      <Separator sx={{ mb: '32px' }} />

      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
          boxShadow: 'none',
          '--Paper-shadow': 'none !important',
          '--Paper-overlay': 'unset !important',
        }}
      >
        <Table sx={{ width: 'unset', tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ height: '42px' }}>
              <TableCell
                sx={{
                  padding: 0,
                  width: '200px',
                  pl: 0,
                  pr: 3,
                  pb: 2,
                  fontWeight: 600,
                  borderBottom: 'none',
                }}
                tabIndex={0}
              >
                Notification type
              </TableCell>

              <TableCell
                align="center"
                sx={{
                  width: '42px',
                  fontWeight: 600,
                  padding: 0,
                  borderBottom: 'none',
                  pr: 3,
                  pb: 2,
                }}
                tabIndex={0}
              >
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="42px"
                >
                  Push
                </Box>
              </TableCell>

              <TableCell
                align="center"
                sx={{
                  width: '42px',
                  fontWeight: 600,
                  padding: 0,
                  borderBottom: 'none',
                  pb: 2,
                }}
                tabIndex={0}
              >
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="42px"
                >
                  Email
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {notificationsTypes.map((notificationType) =>
              renderRow(notificationType)
            )}
          </TableBody>
        </Table>
      </Box>
    </Card>
  );
};

const SettingsType = PropTypes.shape({
  push: PropTypes.shape({}),
  email: PropTypes.shape({}),
});

NotificationsTable.propTypes = {
  settings: SettingsType.isRequired,
  toggleSetting: PropTypes.func.isRequired,
  isFetchingSettings: PropTypes.bool,
  isSavingSettings: PropTypes.bool,
};

export default NotificationsTable;
